import {
  Avatar,
  Banner,
  BoundNumberField,
  Button,
  ButtonMenu,
  Chip,
  Css,
  GridColumn,
  GridTable,
  HbLoadingSpinner,
  Icon,
  SelectField,
  SimpleHeaderAndData,
  Switch,
  Tag,
  column,
  dateColumn,
  emptyCell,
  numericColumn,
  simpleDataRows,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { useFormState } from "@homebound/form-state";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { usePrevious } from "react-use";
import {
  addEntityParam,
  createDesignPackageUrl,
  createPlanPackageUrl,
  createProductOfferingApplyToProjectsUrl,
  createProductOfferingConfigEditUrl,
  createProductOfferingConfigUrl,
  createProductOfferingConfigsUrl,
  createProductOfferingCostCalculatorUrl,
  createProductOfferingEditUrl,
  createProductOfferingScopeUrl,
  createProductOfferingVersionHistoryUrl,
  createProjectProductOfferingVersionHistoryUrl,
} from "src/RouteUrls";
import { StaticHeaderField, dateCell, priceCell } from "src/components";
import { formatDate } from "src/components/FormattedDate";
import { ReadyPlanVersionHeader } from "src/components/ReadyPlanVersionHeader";
import { CollapseRowToggle } from "src/components/formFields/CollapseRowToggle";
import { baseDownloadUrl } from "src/context";
import { useTrackedJobs } from "src/contexts/TrackedJobsContext";
import { AggregateVersionStatus, ReadyPlanStatus } from "src/generated/graphqlTypes";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import { useRouteParams } from "src/hooks/useRouteParams";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import {
  ConfigProductOfferingPageFragment,
  OptionProductOfferingPageFragment,
  OptionTypeProductOfferingPageFragment,
  ProductOfferingDetailFragment,
  useProductOfferingPageQuery,
  useProductOffering_InsideMapsPacketLazyQuery,
  useProductOffering_PromoteToCheckoutMutation,
  useProductOffering_RunCheckoutPricingReportMutation,
  useProductOffering_SaveProductOfferingMutation,
  type OptionGroupProductOfferingPageFragment,
} from "src/routes/developments/product-offerings/ProductOfferingPage.generated";
import { PageHeader } from "src/routes/layout/PageHeader";
import { PublishAggregateVersionModal } from "src/routes/libraries/plan-package/details/components/PublishAggregateVersionModal";
import { PLAN_FALLBACK_IMG } from "src/routes/libraries/plan-package/takeoffs/utils";
import { ProductOfferingParams } from "src/routes/routesDef";
import { fail, pluralize, queryResult } from "src/utils";
import { downloadJson } from "src/utils/downloader";
import { programDataBathRange, programDataRange } from "src/utils/programData";
import { openInSelf } from "src/utils/window";
import { FilteredProjectsTable } from "./FilteredProjectsTable";
import { BidOutCell } from "./components/BidOutCell";
import { DesignPackageTable } from "./components/DesignPackageTable";
// eslint-disable-next-line @homebound/prevent-test-file-imports-in-app-code
import { TestGroupDependenciesModal } from "./components/TestGroupDependenciesModal";
import { UpdatesAvailableButton } from "./components/UpdatesAvailable";

export function ProductOfferingPage() {
  const { productOfferingId, rpavId } = useRouteParams<ProductOfferingParams>();
  const query = useProductOfferingPageQuery({ variables: { id: productOfferingId, versionId: rpavId } });
  const navigateToRpav = useNavigateToRpav();
  const prevProductOffering = usePrevious(query.data?.productOffering);
  const { addJobParent } = useTrackedJobs();

  // Register the parent job for progress tracking (including failed jobs so users see the error)
  const { trackedJobs, name } = query.data?.productOffering ?? {};
  useEffect(() => {
    if (trackedJobs?.nonEmpty) {
      addJobParent(productOfferingId, `Syncing ${name ?? "Product Offering"}`, query.refetch);
    }
  }, [addJobParent, productOfferingId, name, trackedJobs, query]);

  // Poll for promoteInProgress, or as a fallback when copyInProgress has no tracked job
  useEffect(() => {
    const po = query.data?.productOffering;
    const needsLegacyPoll = po?.promoteInProgress || (po?.copyInProgress && po?.trackedJobs.isEmpty);
    needsLegacyPoll ? query.startPolling(2_500) : query.stopPolling();
  }, [query]);

  // If we have created a new draft version, we should navigate to it
  // We have to wait until the sync job finishes to know the id of the rpav, that's why we cant just trigger this after accepting the updates
  useEffect(() => {
    // If we are no longer copying, and we were previously copying, and we have a new draft, navigate to it
    if (
      prevProductOffering?.copyInProgress &&
      !query.data?.productOffering.copyInProgress &&
      prevProductOffering.aggregateDraft?.id !== query.data?.productOffering.aggregateDraft?.id
    ) {
      navigateToRpav(query.data?.productOffering.aggregateDraft?.id);
    }
  }, [query, prevProductOffering, navigateToRpav]);

  return queryResult(query, {
    data: ({ productOffering }) => {
      return <ProductOfferingPageView productOffering={productOffering} rpavId={rpavId} />;
    },
  });
}
type ProductOfferingPageViewProps = { productOffering: ProductOfferingDetailFragment; rpavId?: string };

function ProductOfferingPageView(props: ProductOfferingPageViewProps) {
  const { productOffering, rpavId } = props;
  const developmentId = productOffering.development?.id;
  const projectId = productOffering.project?.id;
  const versionId = rpavId ?? productOffering.version.id;
  const isArchived = productOffering.status.code === ReadyPlanStatus.Archived;
  // Can edit when viewing the draft, or viewing active with no draft
  const isEditable =
    productOffering.version.id === productOffering.aggregateDraft?.id ||
    (productOffering.version.id === productOffering.aggregateActive?.id && !productOffering.aggregateDraft);

  const [showNewOptions, setShowNewOptions] = useState(true);
  const [saveProductOffering] = useProductOffering_SaveProductOfferingMutation();
  const [runPriceReport] = useProductOffering_RunCheckoutPricingReportMutation();
  const [fetchInsideMapsPacket] = useProductOffering_InsideMapsPacketLazyQuery();

  const { openModal } = useModal();
  const { addJob } = useTrackedJobs();
  const tid = useTestIds({});
  const types = productOffering.options
    .filter((o) => o.active)
    .map((o) => o.type)
    // Filter out interior design package options because they have a unique display
    .filter((t) => !t.forDesignInterior)
    .unique();
  const optionTypeGroups = productOffering.options.filter((rpo) => rpo.active).groupBy((o) => o.type.id);
  const coverPhotoUrl = productOffering.coverAsset?.previewUrl ?? PLAN_FALLBACK_IMG;
  const viewingLatestDraft = productOffering.version.id === productOffering.aggregateDraft?.id;
  const isViewingCheckoutVersion = productOffering.version.id === productOffering.aggregateCheckout?.id;
  const hasBeenPromotedToCheckout = productOffering.version.promotedAt != null;
  const canPromoteToCheckout = productOffering.version.canPromote.allowed;
  const canRestoreToCheckout =
    hasBeenPromotedToCheckout && !isViewingCheckoutVersion && !productOffering.promoteInProgress && !isArchived;
  const canUseCheckoutAction = canPromoteToCheckout || canRestoreToCheckout;

  const newOptionsFromUpstreamCount =
    productOffering.aggregateDraft?.optionVersions.filter((o) => o.isNewFromUpstream).length || 0;

  const formState = useFormState({
    config: { contingencyMarkupPercentage: { type: "value" } },
    init: {
      input: productOffering,
      map: (productOffering) => ({ contingencyMarkupPercentage: productOffering.contingencyMarkupPercentage }),
    },
    autoSave: async ({ changedValue: { contingencyMarkupPercentage } }) => {
      contingencyMarkupPercentage !== undefined &&
        (await saveProductOffering({ variables: { input: { id: productOffering.id, contingencyMarkupPercentage } } }));
    },
  });

  const checkoutActionDisabledReason =
    !canUseCheckoutAction && !canPromoteToCheckout
      ? productOffering.version.canPromote.disabledReasons.map((r) => r.message).join(", ")
      : false;

  return (
    <div>
      <PageHeader
        title={productOffering.name}
        titleSize="xl2"
        right={
          <>
            {(productOffering.copyInProgress || productOffering.promoteInProgress) && (
              <div css={Css.df.fdr.aic.mdSb.red400.pr4.gap2.$}>
                <div css={Css.maxwPx(40).maxhPx(40).$}>
                  <HbLoadingSpinner iconOnly />
                </div>
                {productOffering.copyInProgress ? "Sync in progress..." : "Promotion in progress..."}
              </div>
            )}
            <UpdatesAvailableButton
              copyInProgress={productOffering.copyInProgress}
              updates={productOffering.availableUpdates}
              productOfferingId={productOffering.id}
              isArchived={isArchived}
            />
            <ReadyPlanVersionHeader
              readyPlan={productOffering}
              versionId={productOffering.version.id}
              aggregateCheckoutId={productOffering.aggregateCheckout?.id}
            />
            <Button
              variant="secondary"
              size="md"
              label={
                hasBeenPromotedToCheckout && !isViewingCheckoutVersion ? "Restore to Checkout" : "Promote to Checkout"
              }
              disabled={checkoutActionDisabledReason}
              onClick={() =>
                openModal({
                  content: canPromoteToCheckout ? (
                    <PromoteToCheckoutModal aggregateVersionId={productOffering.version.id} />
                  ) : canRestoreToCheckout ? (
                    <RestoreToCheckoutModal
                      productOfferingId={productOffering.id}
                      aggregateVersionId={productOffering.version.id}
                    />
                  ) : null,
                })
              }
            />
            <Button
              size="md"
              disabled={!productOffering.aggregateDraft || productOffering.copyInProgress || isArchived}
              label="Publish"
              onClick={() =>
                openModal({
                  content: (
                    <PublishAggregateVersionModal
                      draftAggregateVersion={productOffering.aggregateDraft ?? fail("No draft aggregate version found")}
                      copyText="This update and version notes will be made available to any active projects that reference it."
                      readyPlanName={productOffering.name}
                      readyPlanId={productOffering.id}
                      allowGenerateAiNotes
                    />
                  ),
                })
              }
            />
            <ButtonMenu
              trigger={{ icon: "verticalDots" }}
              items={[
                ...(versionId
                  ? [
                      {
                        label: "Apply to Projects",
                        disabled:
                          productOffering.status.code === ReadyPlanStatus.Draft
                            ? "Cannot apply a draft Product Offering"
                            : false,
                        onClick: createProductOfferingApplyToProjectsUrl(
                          productOffering.id,
                          versionId,
                          developmentId,
                          projectId,
                        ),
                      },
                    ]
                  : []),
                {
                  label:
                    isEditable && productOffering.canEdit.allowed && productOffering.canEditOptions?.allowed
                      ? "Edit Offering"
                      : "View Offering",
                  onClick: createProductOfferingEditUrl(
                    productOffering.id,
                    productOffering.version.id,
                    developmentId,
                    projectId,
                  ),
                },
                {
                  label: "Version History",
                  onClick: developmentId
                    ? createProductOfferingVersionHistoryUrl(productOffering.id, developmentId)
                    : createProjectProductOfferingVersionHistoryUrl(productOffering.id, projectId!),
                },
                {
                  label: "Create Configurations",
                  onClick: createProductOfferingConfigUrl({
                    idOrAdd: addEntityParam,
                    // Update url params with metaIds to load the page with selectable options & delta costs
                    productOfferingId: productOffering.id,
                    developmentId,
                    projectId,
                  }),
                },
                {
                  label: "Compare Configurations",
                  onClick: createProductOfferingConfigsUrl(developmentId, projectId, productOffering.id, versionId),
                },
                {
                  label: "Download Price Report",
                  onClick: async () => {
                    const { data } = await runPriceReport({
                      variables: { input: { pofId: productOffering.id, rpavId: versionId } },
                    });
                    const jobId = data?.runCheckoutPricingReport.report.id;
                    if (jobId) {
                      addJob(jobId, `Generating Price Report for ${productOffering.name}`);
                    }
                  },
                },
                {
                  label: "Download Cost Report",
                  onClick: () => {
                    const params = new URLSearchParams({
                      type: "offeringCostReport",
                      pofId: productOffering.id,
                      rpavId: versionId,
                    });
                    openInSelf(`${baseDownloadUrl()}/xlsx?${params.toString()}`);
                  },
                },
                {
                  label: "Download Cost Diff Report",
                  onClick: () =>
                    openModal({
                      content: (
                        <CostDiffReportModal
                          productOfferingId={productOffering.id}
                          aggregateVersions={productOffering.aggregateVersions}
                        />
                      ),
                    }),
                },
                {
                  label: "Download Option Cost Report",
                  onClick: () => {
                    const params = new URLSearchParams({
                      type: "offeringOptionCostReport",
                      pofId: productOffering.id,
                      rpavId: versionId,
                    });
                    openInSelf(`${baseDownloadUrl()}/xlsx?${params.toString()}`);
                  },
                },
                {
                  label: "Download InsideMaps Packet",
                  onClick: async () => {
                    const { data } = await fetchInsideMapsPacket({ variables: { id: productOffering.id, versionId } });
                    const pof = data?.productOffering;
                    const maybeCommunity = pof?.project?.name ? `${pof.project.name} - ` : "";
                    downloadJson(
                      `${maybeCommunity}${pof?.name} - InsideMaps Packet - v${pof?.version.version}.json`,
                      pof?.insideMapsPacket ?? {},
                    );
                  },
                },
                {
                  label: "Archive",
                  onClick: () =>
                    openModal({ content: <ArchiveOfferingModal productOfferingId={productOffering.id} /> }),
                  disabled: isArchived ? "Product Offering is already Archived" : false,
                },
                {
                  label: "Test Group Dependencies",
                  onClick: () =>
                    openModal({ content: <TestGroupDependenciesModal productOffering={productOffering} /> }),
                },
              ]}
            />
          </>
        }
        left={isArchived && <Tag text="Archived" type="warning" />}
      />
      {productOffering.checkoutBlockingErrors.nonEmpty && (
        <div css={Css.mb3.$} {...tid.checkoutBlockingErrors}>
          <Banner
            type="warning"
            message={
              <div css={Css.df.fdc.gap1.$}>
                <div css={Css.smSb.$}>
                  This Product Offering cannot be promoted to Checkout until the following issues are resolved:
                </div>
                <ul css={Css.m0.pl3.$}>
                  {productOffering.checkoutBlockingErrors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </div>
            }
          />
        </div>
      )}
      <div css={Css.bshBasic.br8.df.oh.bgWhite.$} {...tid.imageAsset}>
        <img src={coverPhotoUrl} alt="Product Offering cover photo" {...tid.coverPhoto} css={Css.w50.ha.$} />

        <div css={Css.mlPx(58).mtPx(79).$}>
          <div css={Css.dg.gtc("minmax(auto, 1fr) minmax(auto, 1fr)").mlPx(-32).$}>
            <div css={Css.py2.px4.bb.bcGray200.$}>
              <StaticHeaderField
                label="Sq Ft"
                value={
                  <div css={Css.xl.$}>
                    {programDataRange(productOffering.minProgramData, productOffering.maxProgramData, "sellableSqft")}
                  </div>
                }
              />
            </div>
            <div css={Css.py2.px4.bl.bcGray200.bb.$}>
              <StaticHeaderField
                label="Bid Out"
                value={<div css={Css.xl.$}>{`${productOffering.bidOutPercentage}%`}</div>}
              />
            </div>
            <div css={Css.py2.px4.$}>
              <StaticHeaderField
                label="Beds"
                value={
                  <div css={Css.xl.$}>
                    {programDataRange(productOffering.minProgramData, productOffering.maxProgramData, "bedrooms")}
                  </div>
                }
              />
            </div>
            <div css={Css.py2.px4.bl.bcGray200.$}>
              <StaticHeaderField
                label="Baths"
                value={
                  <div css={Css.xl.$}>
                    {programDataBathRange(productOffering.minProgramData, productOffering.maxProgramData)}
                  </div>
                }
              />
            </div>
          </div>
          <div css={Css.df.fdc.py1.gap1.$}>
            <Switch
              label="Publish to Underwriting"
              selected={productOffering.publishToUnderwriting}
              onChange={async (shouldPublish) => {
                await saveProductOffering({
                  variables: { input: { id: productOffering.id, publishToUnderwriting: shouldPublish } },
                });
              }}
            />
            <Switch
              label="Show in Checkout"
              selected={productOffering.showInCheckout}
              onChange={async (shouldShow) => {
                await saveProductOffering({
                  variables: { input: { id: productOffering.id, showInCheckout: shouldShow } },
                });
              }}
            />
          </div>
          <div css={Css.wPx(180).$}>
            <BoundNumberField
              type="percent"
              label="Contingency"
              placeholder="None"
              field={formState.contingencyMarkupPercentage}
              compact
              numFractionDigits={2}
              labelStyle="left"
              disabled={
                (!productOffering.aggregateDraft || productOffering.copyInProgress || isArchived) &&
                "Can only be edited on a Draft version"
              }
            />
          </div>
          <div css={Css.my2.$}>
            <div css={Css.gap3.df.fdr.$}>
              {productOffering.planPackage.aggregateLatest && (
                <Button
                  variant="text"
                  onClick={createPlanPackageUrl(
                    productOffering.planPackage.id,
                    productOffering.planPackage.aggregateLatest.id,
                  )}
                  label={
                    <div css={Css.df.aic.gap1.$}>
                      <span>Plan Package</span>
                      <Chip text={productOffering.planPackage.aggregateLatest.displayVersion} />
                    </div>
                  }
                  endAdornment={<Icon icon="chevronRight" inc={3} />}
                />
              )}
              {productOffering.exteriorDesignPackage?.aggregateLatest && (
                <Button
                  variant="text"
                  onClick={createDesignPackageUrl(
                    productOffering.exteriorDesignPackage.id,
                    productOffering.exteriorDesignPackage.aggregateLatest.id,
                  )}
                  label={
                    <div css={Css.df.aic.gap1.$}>
                      <span>Exterior Design Package</span>
                      <Chip text={productOffering.exteriorDesignPackage.aggregateLatest.displayVersion} />
                    </div>
                  }
                  endAdornment={<Icon icon="chevronRight" inc={3} />}
                />
              )}
            </div>
            <div css={Css.gap3.df.fdr.mt2.$}>
              {productOffering.interiorDesignPackage.aggregateLatest && (
                <Button
                  variant="text"
                  onClick={createDesignPackageUrl(
                    productOffering.interiorDesignPackage.id,
                    productOffering.interiorDesignPackage.aggregateLatest.id,
                  )}
                  label={
                    <div css={Css.df.aic.gap1.$}>
                      <span>Interior Design Package</span>
                      <Chip text={productOffering.interiorDesignPackage.aggregateLatest.displayVersion} />
                    </div>
                  }
                  endAdornment={<Icon icon="chevronRight" inc={3} />}
                />
              )}
              {productOffering.aggregateDraft?.tliVersionsCount && viewingLatestDraft ? (
                <Button
                  variant="text"
                  onClick={createProductOfferingScopeUrl(productOffering.id, productOffering.version.id, {
                    developmentId,
                    projectId,
                  })}
                  label={
                    <div css={Css.df.gap("2px").$}>
                      <div css={Css.smSb.$}>Scope</div>
                      <Tag
                        type="update"
                        text={<div css={Css.xsSb.$}>{productOffering.aggregateDraft?.tliVersionsCount}</div>}
                        preventTooltip
                      />
                    </div>
                  }
                  endAdornment={<Icon icon="chevronRight" inc={3} />}
                />
              ) : (
                <Button
                  variant="text"
                  onClick={createProductOfferingScopeUrl(productOffering.id, productOffering.version.id, {
                    developmentId,
                    projectId,
                  })}
                  label="Scope"
                  endAdornment={<Icon icon="chevronRight" inc={3} />}
                />
              )}
              <Button
                variant="text"
                onClick={createProductOfferingCostCalculatorUrl(productOffering.id, productOffering.version.id)}
                label="Cost Calculator"
                endAdornment={<Icon icon="chevronRight" inc={3} />}
              />
            </div>
          </div>
        </div>
      </div>

      <div css={Css.xl.mt6.mb1.$}>Offering Summary</div>
      {newOptionsFromUpstreamCount > 0 &&
        viewingLatestDraft &&
        showNewOptions &&
        productOffering.aggregateDraft?.version !== 1 && (
          <div css={Css.mb("12px").$} {...tid.newOptionsBanner}>
            <Banner
              type="warning"
              message={
                <>
                  There {pluralize(newOptionsFromUpstreamCount, "is", "are")} {newOptionsFromUpstreamCount} new{" "}
                  {pluralize(newOptionsFromUpstreamCount, "option")} that you can&nbsp;
                  <Link
                    css={Css.tdu.$}
                    to={createProductOfferingEditUrl(
                      productOffering.id,
                      productOffering.version.id,
                      developmentId,
                      projectId,
                    )}
                  >
                    add to the Offering
                  </Link>
                </>
              }
              showIcon={false}
              onClose={() => setShowNewOptions(false)}
            ></Banner>
          </div>
        )}
      <div css={Css.df.fdc.gap2.$}>
        <DesignPackageTable
          designPackageCombinations={productOffering.designPackageCombinations}
          developmentId={developmentId}
          projectId={projectId}
        />
        {types.sortByKey("order").map((type) => {
          const options = optionTypeGroups[type.id];
          return (
            <div key={type.id} css={Css.br8.oh.$}>
              <OptionTypeGroupTable type={type} options={options} developmentId={developmentId} projectId={projectId} />
            </div>
          );
        })}
      </div>
      <div css={Css.xl.mb1.mt6.$}>Offering Usage</div>
      <div css={Css.w100.bgWhite.br8.$}>
        <FilteredProjectsTable projectFilter={{ readyPlans: [productOffering.id] }} />
      </div>
      <div css={Css.xl.mb1.mt6.$}>Saved Configurations</div>
      <div css={Css.w100.bgWhite.br8.oh.$}>
        <ConfigsTable productOffering={productOffering} />
      </div>
    </div>
  );
}

type ConfigsTableProps = { productOffering: ProductOfferingDetailFragment };

function ConfigsTable({ productOffering }: ConfigsTableProps) {
  const columns = createConfigColumns();
  return (
    <GridTable<ConfigRow>
      columns={columns}
      rowStyles={{
        data: {
          rowLink: ({ id }) =>
            createProductOfferingConfigEditUrl({
              productOfferingConfigId: id,
              // Update url params with metaIds to load the page with selectable options & delta costs
              productOfferingId: productOffering.id,
              developmentId: productOffering.development?.id,
              projectId: productOffering.project?.id,
            }),
        },
      }}
      rows={simpleDataRows(productOffering.configs)}
      sorting={{ on: "client" }}
      fallbackMessage="There aren't any configurations to display."
    />
  );
}

type ConfigRow = SimpleHeaderAndData<ConfigProductOfferingPageFragment>;

const createConfigColumns = (): GridColumn<ConfigRow>[] => [
  column<ConfigRow>({ header: "Name", data: ({ name }) => name, w: "500px" }),
  column<ConfigRow>({
    header: "Created by",
    data: ({ createdBy }) => ({
      content: <Avatar src={createdBy.internalUser?.avatar} name={createdBy.internalUser?.name} showName size="sm" />,
      value: createdBy.internalUser?.name,
    }),
  }),
  dateColumn<ConfigRow>({ header: "Created", data: ({ createdAt }) => dateCell(createdAt) }),
  column<ConfigRow>({
    header: "Published to Underwriting",
    data: ({ publishToUnderwriting }) => ({
      content: publishToUnderwriting ? () => <Icon icon="checkboxChecked" /> : "",
      sortValue: publishToUnderwriting ? 1 : 0,
    }),
  }),
  numericColumn<ConfigRow>({
    header: "Est. Cost",
    data: ({ totalCostInCents }) => priceCell({ valueInCents: totalCostInCents }),
  }),
];

type OptionRow = { kind: "head" } | { kind: "data"; data: OptionProductOfferingPageFragment; id: string };
type OptionGroupRow = {
  id: string;
  kind: "group";
  data: OptionGroupProductOfferingPageFragment;
  children: OptionRow[];
};
type TypeRow = { id: string; kind: "type"; data: OptionTypeProductOfferingPageFragment };
type OptionTypeRow = TypeRow | OptionRow | OptionGroupRow;

type OptionTypeGroupTableProps = {
  type: OptionTypeProductOfferingPageFragment;
  options: OptionProductOfferingPageFragment[];
  developmentId?: string;
  projectId?: string;
};

function OptionTypeGroupTable({ type, options, developmentId, projectId }: OptionTypeGroupTableProps) {
  if (options.isEmpty) return null;
  const isPersonalizationType = type.isPackageOptionV2 || type.isProductOptionV2;
  const rows = [
    {
      kind: "type" as const,
      id: type.id,
      data: type,
      children: [
        { kind: "head" as const, id: `head-${type.id}`, data: {} },
        ...(isPersonalizationType
          ? options
              .groupByObject((o) => o.optionGroup)
              .map(([g, options]) => ({
                id: g.id,
                kind: "group" as const,
                data: g,
                children: options.map((o) => ({ id: o.id, data: o, kind: "data" as const })),
              }))
          : options.map((o) => ({ id: o.id, data: o, kind: "data" as const }))),
      ],
    },
  ];
  const columns = createOptionColumns(
    options.some((o) => o.programData?.sellableSqft),
    developmentId,
    projectId,
  );
  return (
    <GridTable<OptionTypeRow>
      rowStyles={{ type: { cellCss: Css.py3.$ }, head: { cellCss: Css.xs.py1.$ } }}
      columns={columns}
      rows={rows}
    />
  );
}

const createOptionColumns = (
  showSqft: boolean,
  developmentId?: string,
  projectId?: string,
): GridColumn<OptionTypeRow>[] => [
  column<OptionTypeRow>({
    head: "Option Name",
    data: ({ name }) => name,
    group: ({ name }) => ({ content: name, css: Css.gray700.smSb.$ }),
    type: ({ name }, { row }) => ({
      content: (
        <div css={Css.df.gap2.$}>
          <div css={Css.lg.$}>{name}</div>
          {/* Don't count the header row in child count */}
          <Chip text={String(row.children.length - 1)} />
        </div>
      ),
      colspan: 3,
    }),
    w: "500px",
  }),
  column<OptionTypeRow>({
    head: "Option Code",
    data: ({ globalOption }) => globalOption?.code || emptyCell,
    type: emptyCell,
    group: emptyCell,
  }),
  column<OptionTypeRow>({
    head: showSqft ? "Sq Ft" : emptyCell,
    data: ({ programData }) =>
      showSqft ? (programData?.sellableSqft ? `+${programData.sellableSqft}` : "n/a") : emptyCell,
    type: emptyCell,
    group: emptyCell,
  }),
  column<OptionTypeRow>({
    head: "Published to Underwriting",
    data: ({ publishToUnderwriting }) => (publishToUnderwriting ? <Icon icon="checkboxChecked" /> : emptyCell),
    type: emptyCell,
    group: emptyCell,
    align: "center",
  }),
  column<OptionTypeRow>({
    head: "Show in Checkout",
    data: ({ showInCheckout }) => (showInCheckout ? <Icon icon="checkboxChecked" /> : emptyCell),
    type: emptyCell,
    group: emptyCell,
    align: "center",
  }),
  column<OptionTypeRow>({
    head: "Cost Source",
    group: emptyCell,
    data: ({ bidOutPercentage }) => (
      <BidOutCell bidOutPercentage={bidOutPercentage} developmentId={developmentId} projectId={projectId} />
    ),
    // TODO: add percentage here
    type: (data, { row, api }) => (
      <div css={Css.mla.mr2.df.aic.$}>
        <CollapseRowToggle label="" rowId={row.id} api={api} />
      </div>
    ),
  }),
];

function ArchiveOfferingModal({ productOfferingId }: { productOfferingId: string }) {
  const [saveProductOffering] = useProductOffering_SaveProductOfferingMutation();
  const { closeModal } = useModal();

  async function archiveProductOffering() {
    await saveProductOffering({ variables: { input: { id: productOfferingId, status: ReadyPlanStatus.Archived } } });
    closeModal();
  }

  return (
    <ConfirmationModal
      title="Archive Product Offering"
      label="Archive"
      danger
      onConfirmAction={archiveProductOffering}
      confirmationMessage={
        <>
          <div css={Css.smSb.mb1.$}>Are you sure you want to archive this Product Offering?</div>
          <div>Once archived, this product offering will not be available for bidding.</div>
        </>
      }
    />
  );
}

function CostDiffReportModal(props: {
  productOfferingId: string;
  aggregateVersions: ProductOfferingDetailFragment["aggregateVersions"];
}) {
  const { productOfferingId, aggregateVersions } = props;
  const { closeModal } = useModal();

  const sorted = aggregateVersions.sortBy((v) => -(v.version ?? 0));
  const activeVersion = sorted.find((v) => v.status.code === AggregateVersionStatus.Active);
  // The last-promoted version is the most recent archived version (the one before active)
  const lastPromotedVersion = sorted.find((v) => v.status.code === AggregateVersionStatus.Archived);

  const [oldRpavId, setOldRpavId] = useState<string | undefined>(lastPromotedVersion?.id);
  const [newRpavId, setNewRpavId] = useState<string | undefined>(activeVersion?.id);

  const versionOptions = sorted.map((v) => ({
    id: v.id,
    name: v.status.code === AggregateVersionStatus.Draft ? `Draft of v${v.version}` : `v${v.version}`,
    promotedAt: v.promotedAt,
    promotedBy: v.promotedBy,
  }));

  function versionMenuLabel(v: (typeof versionOptions)[number]) {
    return (
      <div css={Css.df.fdc.$}>
        <div css={Css.df.gap1.$}>{v.name}</div>
        {v.promotedAt && (
          <div css={Css.xs.gray700.$}>
            Promoted on {formatDate(v.promotedAt, "short")}
            {v.promotedBy && ` by ${v.promotedBy.name}`}
          </div>
        )}
      </div>
    );
  }

  return (
    <ConfirmationModal
      title="Download Cost Diff Report"
      label="Download"
      disabled={!oldRpavId || !newRpavId}
      onConfirmAction={() => {
        const params = new URLSearchParams({
          type: "offeringCostDiffReport",
          pofId: productOfferingId,
          oldRpavId: oldRpavId!,
          newRpavId: newRpavId!,
        });
        openInSelf(`${baseDownloadUrl()}/xlsx?${params.toString()}`);
        closeModal();
      }}
      confirmationMessage={
        <div css={Css.df.fdc.gap2.$}>
          <SelectField
            label="Old Version"
            value={oldRpavId}
            options={versionOptions}
            onSelect={setOldRpavId}
            getOptionMenuLabel={versionMenuLabel}
            autoSort={false}
          />
          <SelectField
            label="New Version"
            value={newRpavId}
            options={versionOptions}
            onSelect={setNewRpavId}
            getOptionMenuLabel={versionMenuLabel}
            autoSort={false}
          />
        </div>
      }
    />
  );
}

function PromoteToCheckoutModal({ aggregateVersionId }: { aggregateVersionId: string }) {
  const [promoteToCheckout] = useProductOffering_PromoteToCheckoutMutation();
  const { closeModal } = useModal();
  return (
    <ConfirmationModal
      title="Promote to Checkout"
      label="Promote"
      onConfirmAction={async () => {
        await promoteToCheckout({
          variables: { input: { aggregateVersionId } },
          refetchQueries: ["ProductOfferingPage"],
        });
        closeModal();
      }}
      confirmationMessage="This will set the current version as the Checkout Version and create a new published version."
    />
  );
}

function RestoreToCheckoutModal(props: { productOfferingId: string; aggregateVersionId: string }) {
  const { productOfferingId, aggregateVersionId } = props;
  const [saveProductOffering] = useProductOffering_SaveProductOfferingMutation();
  const { closeModal } = useModal();
  return (
    <ConfirmationModal
      title="Restore to Checkout"
      label="Restore"
      onConfirmAction={async () => {
        await saveProductOffering({
          variables: { input: { id: productOfferingId, aggregateCheckoutId: aggregateVersionId } },
          refetchQueries: ["ProductOfferingPage"],
        });

        closeModal();
      }}
      confirmationMessage="This will restore the previously promoted version as the Checkout Version."
    />
  );
}
